import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Check, GraduationCap, Banknote, Award, Sparkles, Calendar,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import LeadForm from "@/components/site/LeadForm";
import AtlasPromo from "@/components/site/AtlasPromo";
import { COURSE_LIST } from "@/data/courses";

// Curated list of Pflegekraft-facing courses (B2C)
const RELEVANT_IDS = [
  "ambulanter-pflegeassistent",
  "pflegeassistent-lg1-lg2",
  "pflegehilfskraft-betreuung",
  "praxisanleitung",
  "pdl",
  "wundexperte",
  "palliativ-care",
];

const BENEFITS = [
  { icon: GraduationCap, t: "Anerkannte Qualifikation", d: "AZAV-zertifiziert · gültig in ganz NRW" },
  { icon: Banknote, t: "Mehr Gehalt", d: "Mit jeder Stufe steigt dein Stundensatz" },
  { icon: Award, t: "Karrieresprung", d: "LG1 → LG2 → Praxisanleitung → PDL" },
  { icon: Sparkles, t: "Bildungsgutschein", d: "Für ausgewählte Kurse – z. B. LG1 & LG2" },
];

export default function PflegekraftLanding() {
  useEffect(() => {
    document.title = "Weiterbildung für Pflegekräfte · LG1, LG2, PDL, Praxisanleitung | Dickies-Campus";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
    meta.content = "Mehr Verantwortung. Mehr Gehalt. Anerkannte Weiterbildungen für Pflegekräfte – LG1, LG2, Praxisanleitung, PDL, Wundexperte. AZAV-zertifiziert. Bildungsgutschein für ausgewählte Kurse.";
  }, []);

  const courses = RELEVANT_IDS.map((id) => COURSE_LIST.find((c) => c.id === id)).filter(Boolean);

  return (
    <div className="min-h-screen bg-cream-50 text-sand-800">
      <Header />

      <main className="pt-40 pb-24 lg:pt-48">
        {/* HERO */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-terra" />
              <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-clay-dark">
                Für Pflegekräfte · Karriere &amp; Weiterbildung
              </span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-[4.25rem] leading-[1.02] tracking-tight text-sand-800 font-normal">
              Mehr Verantwortung. <br />
              Mehr Gehalt. <br />
              <span className="text-terra">Mehr Möglichkeiten.</span>
            </h1>
            <p className="mt-7 font-display italic text-xl text-sand-600 leading-snug max-w-xl">
              Über 1.000 Pflegekräfte haben mit Dickies-Campus den nächsten Karriereschritt gemacht.
            </p>
            <p className="mt-5 text-sand-700 text-[15px] sm:text-base leading-[1.7] max-w-xl">
              Egal ob Einstieg, Aufstieg oder Führungsrolle – mit unseren AZAV-zertifizierten Kursen
              wirst du genau dort, wo du hin willst. Bildungsgutschein? Bei LG1 & LG2 meist zu 100 %.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Button asChild className="rounded-full bg-terra hover:bg-terra-dark text-white h-14 px-7 text-base font-semibold">
                <a href="#kurse">
                  Passenden Kurs finden
                  <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.8} />
                </a>
              </Button>
              <Button asChild variant="outline"
                className="rounded-full h-14 px-7 text-base font-medium border border-sand-800 text-sand-800 hover:bg-white bg-transparent">
                <a href="#kontakt">Kostenlose Beratung</a>
              </Button>
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="rounded-3xl bg-white border border-cream-300 shadow-soft p-7">
              <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-brand-red mb-5">
                Warum Dickies-Campus
              </div>
              <ul className="space-y-5">
                {BENEFITS.map((b) => (
                  <li key={b.t} className="flex items-start gap-3.5">
                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-terra/12 text-terra">
                      <b.icon className="h-4 w-4" strokeWidth={1.6} />
                    </span>
                    <div>
                      <div className="font-display text-sand-800 text-[17px] leading-tight">{b.t}</div>
                      <div className="text-[13px] text-sand-600 mt-1 leading-snug">{b.d}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </section>

        {/* Course list */}
        <section id="kurse" className="bg-peach-50 mt-24 lg:mt-32 py-20 lg:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-terra" />
              <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra">Kurse für Pflegekräfte</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.75rem] text-sand-800 font-normal tracking-tight leading-[1.12] max-w-3xl">
              Dein Weg – <span className="text-terra">vom Einstieg bis zur Pflegedienstleitung.</span>
            </h2>

            <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.map((c) => (
                <Link key={c.id} to={`/kurse/${c.id}`}
                  data-testid={`pflegekraft-hub-course-${c.id}`}
                  className="group flex flex-col rounded-3xl bg-white border border-cream-300 shadow-soft hover:shadow-hover hover:-translate-y-1 transition-all p-7">
                  <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-brand-red">
                    {c.duration}
                  </div>
                  <h3 className="mt-3 font-display text-2xl text-sand-800 leading-tight">{c.title}</h3>
                  <p className="mt-2 text-sand-600 text-[14px] leading-snug">{c.subtitle}</p>
                  <div className="mt-5 flex items-center gap-2 text-[12px] text-sand-500">
                    <Calendar className="h-3.5 w-3.5" strokeWidth={1.6} /> {c.nextStart}
                  </div>
                  <div className="mt-5 pt-4 border-t border-cream-200 flex items-center justify-between">
                    <span className="font-display text-lg text-sand-800">{c.price}</span>
                    <span className="text-[11px] uppercase tracking-[0.2em] text-terra font-semibold inline-flex items-center gap-1.5">
                      Ansehen <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Atlas Lead-Magnet · Identitäts-Hook für Pflegekräfte */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20 lg:mt-28">
          <AtlasPromo variant="b2c" testId="pflegekraft-atlas-promo" />
        </section>

        {/* Lead form */}
        <div id="kontakt"><LeadForm /></div>
      </main>

      <Footer />
    </div>
  );
}
